import React, { useRef, useState, useCallback } from "react";
import { Box, type BoxProps, Button, Text, AspectRatio } from "@chakra-ui/react";
import { TriangleUpIcon } from "@chakra-ui/icons";
import { VideoJsonLd } from "next-seo";

type VideoProps = {
  source: string;
  title: string;
  description: string;
  uploadDate?: string;
  duration?: string;
  hideTitle?: boolean; // eslint-disable-line react/boolean-prop-naming
  poster?: string;
  type?: "upload" | "youtube";
  onPlay?: () => void;
  onEnded?: () => void;
} & BoxProps;

export function Video({
  source,
  title,
  description,
  uploadDate,
  duration,
  hideTitle,
  type = "upload",
  poster = "/assets/home/video-poster.webp",
  onPlay,
  onEnded,
  ...props
}: VideoProps) {
  const uploadedVideo = useRef<HTMLVideoElement>(null);
  const [play, setPlay] = useState(false);
  const formattedDate = uploadDate ? new Date(uploadDate).toISOString() : undefined;

  const playVideo = useCallback(() => {
    const playPromise = uploadedVideo.current?.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          uploadedVideo.current?.setAttribute("controls", "true");
          setPlay(true);
        })
        .catch(() => {
          setPlay(false);
        });
    }
  }, []);

  return (
    <>
      <Box position="relative" {...props}>
        <Box
          borderRadius="base"
          overflow="hidden"
          _before={{
            position: "absolute",
            content: type === "youtube" || play ? "none" : "''",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            bg: "rgba(0, 0, 0, 0.4)",
            borderRadius: "base",
            zIndex: type === "youtube" || play ? -1 : 1,
          }}
        >
          {type === "upload" ? (
            <video
              ref={uploadedVideo}
              width="100%"
              poster={poster}
              preload="metadata"
              onPlay={onPlay}
              onEnded={onEnded}
            >
              <source src={source} type="video/mp4" />
            </video>
          ) : (
            <AspectRatio pos="relative" w="100%" ratio={16 / 9}>
              <iframe
                allowFullScreen
                width="100%"
                height="100%"
                src={source}
                title={title}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                style={{ position: "absolute", inset: 0 }}
              />
            </AspectRatio>
          )}
        </Box>

        {type === "upload" && !play && (
          <Button
            aria-label="Play video"
            variant="unstyled"
            pt={title && duration ? [0, 0, 16] : 0}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
            role="group"
            transition=".15s all ease"
            zIndex={2}
            _hover={{ transform: "scale(1.15)" }}
            onClick={playVideo}
          >
            <TriangleUpIcon
              color="white"
              transform="rotate(90deg)"
              bg="new.green.500"
              border={{ base: "8px solid", md: "12px solid" }}
              borderColor={{ base: "new.green.50", md: "new.green.50" }}
              w={{ base: 16, md: 24 }}
              h={{ base: 16, md: 24 }}
              p={{ base: 1, md: 3 }}
              pb={{ base: 2, md: 4 }}
              rounded="50%"
            />
            {title && !hideTitle && (
              <Box
                display={["none", "none", "inline-block"]}
                opacity={0}
                bg="white"
                borderRadius="4px"
                py={3}
                px={4}
                maxW="md"
                mx="auto"
                mt={-2}
                transition=".15s opacity ease"
                shadow="xl"
                zIndex={-1}
                _groupHover={{ opacity: 1, h: "auto" }}
              >
                <Text textStyle="caption1" fontWeight="semibold">
                  {title}
                </Text>
                {duration && (
                  <Text textStyle="caption2" mt="tiny">
                    {duration}
                  </Text>
                )}
              </Box>
            )}
          </Button>
        )}
      </Box>

      <VideoJsonLd
        name={title}
        description={description}
        contentUrl={source}
        embedUrl={source}
        thumbnailUrls={[poster]}
        uploadDate={formattedDate ?? "2022-09-14T08:00:00+08:00"}
      />
    </>
  );
}
